import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { getSdk } from '@/lib/sdk';
import type { Ulid } from '@/types';
import type { GroupVisibility } from '@cc-consulting-nv/ccsdk';

export const groupKeys = {
  all: ['groups'] as const,
  list: () => ['groups', 'list'] as const,
  detail: (ulid: Ulid) => ['groups', ulid] as const,
  posts: (ulid: Ulid) => ['groups', ulid, 'posts'] as const,
  members: (ulid: Ulid) => ['groups', ulid, 'members'] as const,
  moderators: (ulid: Ulid) => ['groups', ulid, 'moderators'] as const,
};

/**
 * List all groups with infinite scroll.
 */
export function useGroups() {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: groupKeys.list(),
    queryFn: async ({ pageParam }) => {
      return sdk.getGroups(pageParam ?? undefined);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Fetch a single group by ULID.
 */
export function useGroup(groupUlid: Ulid | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: groupKeys.detail(groupUlid ?? ''),
    queryFn: () => sdk.getGroup(groupUlid!),
    enabled: Boolean(groupUlid),
  });
}

/** 
 * Create a group with a given name, description, visibility
 */
export function useCreateGroup() {
  const sdk = getSdk();
  const queryClient = useQueryClient();
  
    return useMutation({
      mutationFn: async ({ name, description, visibility }: { name: string; description: string, visibility: GroupVisibility }) => {
        return sdk.createGroup({ name, description, visibility });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: groupKeys.all });
      },
    });
}

/**
 * Paginated posts in a group.
 */
export function useGroupPosts(groupUlid: Ulid | undefined) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: groupKeys.posts(groupUlid ?? ''),
    queryFn: async ({ pageParam }) => {
      return sdk.getGroupPosts(groupUlid!, { cursor: pageParam ?? undefined });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(groupUlid),
  });
}

/**
 * Fetch group members.
 */
export function useGroupMembers(groupUlid: Ulid | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: groupKeys.members(groupUlid ?? ''),
    queryFn: () => sdk.getGroupMembers(groupUlid!),
    enabled: Boolean(groupUlid),
  });
}

/**
 * Join/leave a group.
 */
export function useJoinGroup() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, isJoined }: { groupId: string; isJoined: boolean }) => {
      if (isJoined) {
        await sdk.leaveGroup(groupId);
      } else {
        await sdk.joinGroup(groupId);
      }
      return !isJoined;
    },
    onSuccess: (_data, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) });
      queryClient.invalidateQueries({ queryKey: groupKeys.list() });
    },
  });
}

/**
 * Toggle group favorite.
 */
export function useToggleGroupFavorite() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupUlid: Ulid) => {
      return sdk.toggleGroupFavorite(groupUlid);
    },
    onSuccess: (_data, groupUlid) => {
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupUlid) });
      queryClient.invalidateQueries({ queryKey: groupKeys.list() });
    },
  });
}
